import React, { useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { format, eachMonthOfInterval, startOfMonth, isWithinInterval, endOfMonth, startOfDay } from 'date-fns';
import { useTranslation } from 'react-i18next';

interface FunktionarEngagement {
    personalNumber: string;
    name: string;
    functionText: string;
    startDate: string | null;
    endDate: string | null;
    organizationNumber: string;
    organizationName: string;
}

interface WorkloadChartProps {
  data: FunktionarEngagement[];
  series?: { date: number; count: number }[];
  title?: string;
  countLabel?: string;
}

const WorkloadChart: React.FC<WorkloadChartProps> = ({ data, series, title, countLabel }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const chartData = useMemo(() => {
    if (series) return series;
    if (!data || data.length === 0) return [];

    const today = new Date();
    let minDate = today.getTime();
    let maxDate = today.getTime();

    // Find range
    data.forEach(item => {
      if (item.startDate) {
        const start = new Date(item.startDate).getTime();
        if (start < minDate) minDate = start;
      }
      
      const end = item.endDate ? new Date(item.endDate).getTime() : today.getTime();
      if (end > maxDate) maxDate = end;
    });

    // Generate monthly points
    const start = startOfMonth(new Date(minDate));
    const end = endOfMonth(new Date(maxDate));

    // If start is after end (sanity check), just return empty
    if (start > end) return [];

    const months = eachMonthOfInterval({ start, end });

    return months.map(date => {
      const comparisonDate = startOfDay(date);
      
      const activeCount = data.filter(item => {
        if (!item.startDate) return false;

        const itemStart = startOfDay(new Date(item.startDate)); 
        
        if (item.endDate === null) {

             // Active role
             return itemStart <= comparisonDate;
        } else {
             // Ended role
             const itemEnd = startOfDay(new Date(item.endDate));
             
             // Safety check for invalid data
             if (itemStart > itemEnd) return false;

             return isWithinInterval(comparisonDate, { start: itemStart, end: itemEnd });
        }
      }).length;

      return {
        date: date.getTime(),
        count: activeCount,
        label: format(date, 'MMM yyyy')
      };
    });
  }, [data, series]);

  const ticks = useMemo(() => {
      if (chartData.length === 0) return undefined;
      const min = chartData[0].date;
      const max = chartData[chartData.length - 1].date;
      const startYear = new Date(min).getFullYear();
      const endYear = new Date(max).getFullYear();
      const t = [];
      
      for (let y = startYear; y <= endYear + 1; y++) {
          const d = new Date(y, 0, 1).getTime();
          if (d >= min && d <= max) {
              t.push(d);
          }
      }
      return t.length > 0 ? t : undefined;
  }, [chartData]);

  if (chartData.length === 0) return null;

  return (
    <Box sx={{ width: '100%', height: 300, display: 'flex', flexDirection: 'column' }}>
      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold', fontSize: '0.9rem' }}>
        {title ?? t('personSearch.charts.workload')}
      </Typography>
      <Box sx={{ flex: 1, minHeight: 0 }}><ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={chartData}
          margin={{ top: 10, right: 30, left: 0, bottom: 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
          <XAxis 
            dataKey="date" 
            type="number"
            scale="time"
            domain={['dataMin', 'dataMax']}
            tickFormatter={(value) => format(new Date(value), 'yyyy-01-01')}
            ticks={ticks}
            tick={{ fontSize: 10 }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis 
            allowDecimals={false} 
            tick={{ fontSize: 10 }} 
            axisLine={false}
            tickLine={false}
            domain={[0, 'auto']}
          />
          <Tooltip 
            labelFormatter={(label) => format(new Date(label), series ? 'yyyy-MM-dd' : 'MMMM yyyy')}
            contentStyle={{ backgroundColor: theme.palette.background.paper, fontSize: '12px', borderRadius: 4, border: `1px solid ${theme.palette.divider}` }}
          />
          <Area 
            type={series ? 'stepAfter' : 'monotone'}
            dataKey="count" 
            stroke={theme.palette.primary.main} 
            fill={theme.palette.primary.main} 
            fillOpacity={0.2}
            name={countLabel ?? t('personSearch.activeRoles')}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer></Box>
    </Box>
  );
};

export default WorkloadChart;
